import { useEffect, useId, useRef, useState } from 'react'
import { MagnifyingGlass, X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { Input, Spinner } from '@/ui'
import { useUserSearch, type UserOption } from '../api/reference'

/**
 * Pick a person (farmer, operator, finance) by name, username or roster number — GET /users?q.
 * Accessible combobox: arrow keys move, Enter picks, Escape closes.
 */
export function PersonPicker({
  value,
  onChange,
  placeholder = 'Search by name or roster number',
  filter,
  label,
  invalid,
}: {
  value: UserOption | null
  onChange: (u: UserOption | null) => void
  placeholder?: string
  /** Narrow results, e.g. only farmers (u.farmerCode) or a role. */
  filter?: (u: UserOption) => boolean
  label?: string
  invalid?: boolean
}) {
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  // Inside a dialog the body scrolls and would clip a floating list: show it in the flow there.
  const [inDialog, setInDialog] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250)
    return () => clearTimeout(t)
  }, [q])
  const search = useUserSearch(debounced, open && debounced.length >= 2)
  const results = (search.data?.rows ?? []).filter((u) => (filter ? filter(u) : true))

  if (value) {
    return (
      <div className="flex h-10 items-center justify-between gap-2 rounded-chip border border-rule bg-surface px-3">
        <span className="min-w-0 truncate text-[15px]">
          {value.name}
          {value.farmerCode && <span className="ml-2 font-mono text-[12px] text-muted">{value.farmerCode}</span>}
        </span>
        <button type="button" aria-label={`Clear ${label ?? 'person'}`} onClick={() => onChange(null)} className="text-muted hover:text-ink">
          <X aria-hidden="true" />
        </button>
      </div>
    )
  }

  const pick = (u: UserOption) => {
    onChange(u)
    setQ('')
    setOpen(false)
  }

  return (
    <div ref={boxRef} className="relative">
      <Input
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={label}
        aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
        invalid={invalid}
        value={q}
        placeholder={placeholder}
        leading={<MagnifyingGlass />}
        trailing={search.isFetching ? <Spinner /> : undefined}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setActive(0)
        }}
        onFocus={() => {
          setInDialog(Boolean(boxRef.current?.closest('[role="dialog"]')))
          setOpen(true)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, results.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter' && results[active]) {
            e.preventDefault()
            pick(results[active]!)
          } else if (e.key === 'Escape') setOpen(false)
        }}
      />
      {open && debounced.length >= 2 && (
        <ul
          id={listId}
          role="listbox"
          className={cn(
            'mt-1 max-h-64 w-full overflow-auto rounded-card border border-rule bg-surface py-1',
            inDialog ? 'relative' : 'absolute z-30 shadow-pop',
          )}
        >
          {results.length === 0 && !search.isFetching && (
            <li className="px-3 py-2 text-[14px] text-muted">No one found</li>
          )}
          {results.map((u, i) => (
            <li
              key={u.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(u)
              }}
              onMouseEnter={() => setActive(i)}
              className={cn('flex cursor-pointer items-baseline justify-between gap-2 px-3 py-2 text-[14px]', i === active && 'bg-paper-tint')}
            >
              <span className="truncate">{u.name}</span>
              <span className="shrink-0 font-mono text-[11.5px] text-muted">{u.farmerCode ?? u.username}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
